import {Component, OnInit} from '@angular/core';
import {BaseConfigurableComponent} from '@twpub/shared/components/base';
import {ClientConfigurationScheme, ComponentConfiguration, ComponentInit, InitialConfig} from '@twpub/core/utils';
import {FieldProps, Header} from '@twpub/core/models';
import {ComponentCategory, ControlType} from '@twpub/core/enums';
import {MatDialog} from '@angular/material/dialog';
import {AboutDialogComponent} from '@twpub/shared/components/about-dialog/about-dialog.component';
import {SecurityConfigService} from '@twpub/shared/services/security-config.service';
import {FeatureFlagService, FeedbackModeService, FeedbackService} from '@twpub/core/services';
import {NgClass} from '@angular/common';
import {LogoWrapperComponent} from '@twpub/shared/components/wrappers';
import {ConfigurationSelectorComponent} from '@twpub/shared/components';
import {RouterLink} from '@angular/router';
import {MatIcon} from '@angular/material/icon';
import {MatTooltip} from '@angular/material/tooltip';
import {LanguageSwitcherComponent} from '@twpub/shared/components/language-switcher/language-switcher.component';

@Component({
  selector: 'pub-default-header',
  templateUrl: './default-header.component.html',
  styleUrls: ['./default-header.component.scss'],
  standalone: true,
  imports: [NgClass, LogoWrapperComponent, RouterLink, MatIcon, MatTooltip, LanguageSwitcherComponent]
})
export class DefaultHeaderComponent extends BaseConfigurableComponent implements OnInit, Header {
  static readonly SHOW_FEEDBACK_ICON = 'showFeedbackIcon';

  static Init = class extends ComponentInit {
    readonly scheme = ClientConfigurationScheme.DEFAULT;
    override props: FieldProps[] = [
      this.VISIBLE_FIELD,
      {
        key: DefaultHeaderComponent.SHOW_FEEDBACK_ICON,
        label: 'Show feedback icon',
        controlType: ControlType.Boolean,
        description: 'Display a feedback icon that opens a feedback form dialog'
      }
    ];

    constructor() {
      super(DefaultHeaderComponent, 'DefaultHeaderComponent', 'Default header', [ComponentCategory.Header]);
    }

    createConfig(initialConfig: InitialConfig = undefined) {
      return new ComponentConfiguration<Header>(DefaultHeaderComponent, this.name, initialConfig);
    }
  }

  showAdminLink: boolean = false;
  showLogoutLink: boolean = false;
  showFeedbackLink: boolean = false;

  // i18n labels
  administrationLabel = $localize`:@@header.administration:Administration`;
  aboutLabel = $localize`:@@header.about:About`;
  feedbackLabel = $localize`:@@header.feedback:Send Feedback`;
  signOutLabel = $localize`:@@header.signOut:Sign out`;
  showLayoutSelector = false;

  constructor(private dialog: MatDialog, private securityService: SecurityConfigService,
              private feedbackService: FeedbackService, public feedbackMode: FeedbackModeService, private featureFlagService: FeatureFlagService) {
    super();
  }

  ngOnInit(): void {
    this.doUpdate();
    // Check configuration first, then feature flag, then TermWeb config
    const showFeedbackIconConfig = this.config?.getBoolean(DefaultHeaderComponent.SHOW_FEEDBACK_ICON, false) ?? false;
    if (showFeedbackIconConfig) {
      this.feedbackService.getFeedbackConfig().subscribe(config => {
        this.showFeedbackLink = config?.enabled ?? false;
      });
    }
    this.featureFlagService.waitForReady(() => {
      this.showLayoutSelector = this.featureFlagService.isFeatureEnabled('ConfigurationSelectorComponent');
    });
    this.showAdminLink = this.securityService.isAdmin() || !this.securityService.getConfig().url;
    this.showLogoutLink = this.securityService.isExternalAuth() && this.securityService.isExternalLogout();
  }
  override doUpdate() {
    super.doUpdate();
    this.showAdminLink = this.securityService.isAdmin() || !this.securityService.getConfig().url;
    this.showLogoutLink = this.securityService.isExternalAuth() && this.securityService.isExternalLogout();
  }

  toggleFeedbackMode(): void {
    this.feedbackMode.toggle();
  }

  openAboutDialog(): void {
    this.dialog.open(AboutDialogComponent, {
      width: '800px',
      ariaLabel: 'About TermWebPublish'
    });
  }

  async externalLogout() {
    await this.securityService.externalLogout();
  }
}
